import { useEffect, useState } from 'react';
import { useEvents } from '../../hooks/useEvents';
import { useAuth } from '../../hooks/useAuth';
import { useParams } from 'react-router-dom';
import { IEvent, IGenerateQrCodeResponse } from '../../interfaces/IEvent';
import { HelmetComponent } from '../../components/Helmet';
import { ButtonBack } from '../../components/ButtonBack';
import { Card } from '../../components/Card';
import { TextReveal } from '../../components/TextReveal';
import { List, ListItem } from '../../components/List';
import { Button } from '../../components/Button';
import { QrCodeModal } from '../Home/components/EventCard/QrCodeModal';
import { ChallengeDetailPage, PresenceTheme } from '../../features/presence';

export function Event() {
  const { client, player } = useAuth();
  const { getEventById, getPlayerEventHistory } = useEvents();
  const { eventId } = useParams();

  const [event, setEvent] = useState<IEvent>();
  const [eventHistory, setEventHistory] = useState<IGenerateQrCodeResponse[]>();
  const [qRCodeModalIsOpen, setQRCodeModalIsOpen] = useState<boolean>(false);

  useEffect(() => {
    const fetchEvent = async () => {
      if (eventId) {
        const result = await getEventById(eventId, client.id);
        setEvent(result);
        const history = await getPlayerEventHistory(player.id);
        setEventHistory(history);
      }
    };
    fetchEvent();
  }, [eventId, client]);

  // Check-in/check-out events use the new presence screens.
  if (eventId && event?.validationMode === 'CHECK_IN_OUT') {
    return (
      <PresenceTheme>
        <ChallengeDetailPage eventId={eventId} />
      </PresenceTheme>
    );
  }

  return (
    <>
      <HelmetComponent
        actionTitle={event?.title}
        clientLogoURL={client?.logoURL}
        clientFavicon={client?.favIcon}
        companyName={client?.companyName}
        playerName={player?.name}
        primaryColor={client?.primaryColor}
        secondaryColor={client?.secondaryColor}
        tabTitle={'Detalhes da publicação presencial'}
      />
      <div className="container">
        <div className="row">
          <div className="my-3">
            <ButtonBack title="Publicação" />
          </div>
        </div>
        <div className="row">
          <div className="col-sm-12 col-md-12 col-xl-8">
            <Card>
              <TextReveal
                title={event?.title}
                subtitle={event?.description}
                content={`Gere o seu QR code e valide junto ao seu gestor direto`}
              />
              {event?.rules && (
                <TextReveal content={event.rules} subtitle="Como funciona?" />
              )}
              {event?.moreDetails && (
                <TextReveal content={event.moreDetails} subtitle="Benefícios" />
              )}
              {eventHistory && (
                <List>
                  {eventHistory.map((e: IGenerateQrCodeResponse) => (
                    <ListItem
                      key={e.id}
                      content={(() => {
                        let color = '#000';
                        let label = 'AGUARDANDO APROVAÇÃO';
                        const isExpired =
                          event?.endDate &&
                          new Date() > new Date(event.endDate) &&
                          (!e.validations || e.validations.length === 0);
                        if (isExpired) {
                          color = '#FF0000';
                          label = 'EXPIRADO';
                        } else if (e.validations && e.validations.length > 0) {
                          color = '#28a745';
                          label = 'VALIDADO';
                        } else {
                          color = '#FFA500';
                        }

                        return (
                          <span
                            style={{
                              fontSize: '0.95rem',
                              display: 'flex',
                              alignItems: 'center',
                            }}
                          >
                            <span
                              style={{
                                display: 'inline-block',
                                width: '10px',
                                height: '10px',
                                borderRadius: '50%',
                                backgroundColor: color,
                                marginRight: '8px',
                              }}
                            ></span>
                            <span style={{ color: color }}>{label}</span>
                          </span>
                        );
                      })()}
                      secondaryContent={(() => {
                        const date = new Date(e.createdAt);
                        const day = String(date.getDate()).padStart(2, '0');
                        const month = String(date.getMonth() + 1).padStart(
                          2,
                          '0'
                        );
                        const year = date.getFullYear();
                        return (
                          <span style={{ fontSize: '0.90rem', color: '#666' }}>
                            {`${day}/${month}/${year}`}
                          </span>
                        );
                      })()}
                    />
                  ))}
                </List>
              )}
            </Card>
          </div>
          {event?.startDate &&
            event?.endDate &&
            new Date() >= new Date(event.startDate) &&
            new Date() <= new Date(event.endDate) && (
              <div className="col-sm-12 col-md-12 col-xl-4">
                <div className="my-3 my-xl-0">
                  <Card>
                    <span className="fw-semibold">Ações Disponíveis</span>
                    <Button
                      text="GERAR QR CODE"
                      color="primary"
                      onClick={() => setQRCodeModalIsOpen(true)}
                      style={{ width: '100%', marginTop: '1rem' }}
                    />
                  </Card>
                </div>
                <div className="my-3 my-xl-0">
                  <Card>
                    <span className="fw-semibold">Informação importante</span>
                    <hr />
                    <span>
                      {' '}
                      O QR Code gerado é válido por 24 horas e deve ser
                      utilizado no local do plantão
                    </span>
                  </Card>
                </div>
              </div>
            )}
        </div>
      </div>
      <QrCodeModal
        open={qRCodeModalIsOpen}
        onClose={() => setQRCodeModalIsOpen(false)}
        eventId={event?.id}
      />
    </>
  );
}
